import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Paper,
  IconButton,
} from "@mui/material";
import * as dates from "common/dates";
import * as numbers from "common/numbers";
import Button from "components/common/Button";
import Options from "components/common/Options";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useSnackbar } from "notistack";
import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as submissionsService from "services/submissionsService";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

const layerIdOf = (layer) => layer.layerId ?? layer.id;

const PaymentDetailsModal = ({
  open,
  onClose,
  mode = "create",
  payment,
  onRequestDelete,
}) => {
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [isAmountTextValid, setIsAmountTextValid] = useState(true);
  const [comment, setComment] = useState("");
  const [layerId, setLayerId] = useState(null);

  const resolvedLayers = useSelector(pricingSelectors.selectResolvedLayers);
  const boundLayers = useMemo(
    () => (resolvedLayers ?? []).filter((l) => l?.bound),
    [resolvedLayers]
  );
  const submissionId = useSelector(persistenceSelectors.selectSubmissionId);
  const [createPayment] = submissionsService.useCreatePaymentMutation();
  const [updatePayment] = submissionsService.useUpdatePaymentMutation();
  const [deletePayment] = submissionsService.useDeletePaymentMutation();
  const { enqueueSnackbar } = useSnackbar();

  const isEdit = mode === "edit";
  const originalLayerId = isEdit ? payment?.layerId ?? null : null;

  // Focused once the dialog has opened, after resetForm has loaded the amount being edited
  const amountInputRef = useRef(null);

  // Runs as the dialog starts to open (before it paints), so a background refresh of the layers can't wipe what's been typed
  const resetForm = () => {
    if (isEdit && payment) {
      setPaymentDate(dates.toISODateString(payment.paymentDate ?? "") ?? "");
      setPaymentAmount(payment.paymentAmount ?? null);
      setComment(payment.comment ?? "");
      setLayerId(payment.layerId ?? null);
    } else if (!isEdit) {
      setPaymentDate(dates.toISODateString(dates.getDate()) ?? "");
      setPaymentAmount(null);
      setComment("");
      // With a single bound layer there's nothing to choose, so preselect it
      setLayerId(boundLayers.length === 1 ? layerIdOf(boundLayers[0]) : null);
    }
    setIsAmountTextValid(true);
  };

  // Negative amounts are allowed, they record a refund
  const isLayerMissing = !isEdit && layerId == null;
  const isSaveDisabled =
    !paymentDate ||
    paymentAmount == null ||
    !isAmountTextValid ||
    isLayerMissing;

  const saveEdit = async (payload) => {
    const paymentId = payment.paymentId;
    const layerChanged =
      originalLayerId != null &&
      layerId != null &&
      String(originalLayerId) !== String(layerId);
    if (!layerChanged) {
      return updatePayment({ ...payload, layerId: originalLayerId, paymentId });
    }
    // Moving a payment to another layer creates it there, then removes the original
    const created = await createPayment(payload);
    if (created?.error) return created;
    return deletePayment({ submissionId, layerId: originalLayerId, paymentId });
  };

  const handleSave = async () => {
    if (isSaveDisabled) return;

    const payload = {
      submissionId,
      layerId,
      paymentDate,
      paymentAmount,
      comment,
    };
    try {
      const response =
        isEdit && payment?.paymentId
          ? await saveEdit(payload)
          : await createPayment(payload);
      if (response?.error) {
        enqueueSnackbar(`Failed to ${isEdit ? "update" : "create"} payment`, {
          variant: "error",
          autoHideDuration: 5000,
        });
        return;
      }
      enqueueSnackbar(isEdit ? "Payment updated" : "Payment created", {
        variant: "success",
        autoHideDuration: 3000,
      });
      onClose();
    } catch (e) {
      enqueueSnackbar(
        `Unexpected error ${isEdit ? "updating" : "creating"} payment`,
        { variant: "error", autoHideDuration: 5000 }
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"sm"}
      fullWidth
      slotProps={{
        transition: {
          onEnter: resetForm,
          onEntered: () => amountInputRef.current?.focus(),
        },
      }}
    >
      <DialogTitle>
        {isEdit ? "Edit Payment" : "Create Payment"}
        <IconButton
          aria-label={"close"}
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size={"small"}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Paper elevation={0} sx={{ padding: "1rem" }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={"row"} spacing={2}>
              <PrettyNumberTextField
                label={"Amount"}
                variant={"outlined"}
                inputRef={amountInputRef}
                value={paymentAmount}
                onChangeNumber={setPaymentAmount}
                onValidation={setIsAmountTextValid}
                offFormatter={numbers.dpString(2)}
                fullWidth
                sx={{ flex: 1 }}
                required
                error={!isAmountTextValid}
                helperText={
                  !isAmountTextValid
                    ? "Enter a valid number"
                    : paymentAmount < 0
                    ? "Negative amount, recorded as a refund"
                    : ""
                }
              />
              <TextField
                label={"Date"}
                type={"date"}
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ flex: 1 }}
                required
              />
            </Stack>
            <TextField
              label={"Comment"}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            {boundLayers.length > 0 ? (
              <Stack spacing={0.5}>
                <Typography variant={"body2"} color={"text.secondary"}>
                  {"Layer"}
                </Typography>
                <Options
                  items={boundLayers.map((l) => {
                    const id = layerIdOf(l);
                    const label =
                      numbers.cleanXS({
                        limit: l?.limit,
                        attachment: l?.attachment,
                      }) || id;
                    return { id, label, selected: layerId === id };
                  })}
                  onClick={({ id }) => setLayerId(layerId === id ? null : id)}
                  variant={"button"}
                />
              </Stack>
            ) : (
              <Typography variant={"body2"} color={"text.secondary"}>
                {"No bound layers found in the submission"}
              </Typography>
            )}
          </Stack>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isEdit && onRequestDelete && (
          <Box sx={{ mr: "auto" }}>
            <Button
              onClick={() => onRequestDelete(payment)}
              color={"error"}
              sx={{ width: 100 }}
            >
              {"Delete"}
            </Button>
          </Box>
        )}
        <Button
          onClick={handleSave}
          color={"primary"}
          sx={{ width: 100 }}
          disabled={isSaveDisabled}
        >
          {"Save"}
        </Button>
        <Button onClick={onClose} color={"secondary"} sx={{ width: 100 }}>
          {"Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDetailsModal;
